import React from 'react';
import type {Intent} from '@sail/engine';
import {view} from '@sail/ui';
import {ViewportContext} from './ViewportContext';
import {
  BreakpointSize,
  connectElementBreakpoints,
  defaultBreakpoint,
} from './breakpoints';

type Props = {
  styles?: Intent[];
};

export const ViewportProvider: React.FC<Props> = ({children, styles = []}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [breakpoint, setBreakpoint] =
    React.useState<BreakpointSize>(defaultBreakpoint);
  const [containerSize, setContainerSize] = React.useState({
    width: 0,
    height: 0,
  });

  const handleResizeChange = React.useCallback(() => {
    if (containerRef?.current) {
      const clientRects = containerRef.current.getClientRects();
      if (clientRects.length > 0) {
        const {width, height} = containerRef.current.getClientRects()[0];
        setContainerSize({
          width,
          height,
        });
        const currentBreakpoint = connectElementBreakpoints.find(
          ({breakpoint}) => width <= breakpoint,
        );
        setBreakpoint(currentBreakpoint?.name ?? defaultBreakpoint);
      }
    }
  }, []);

  // Set correct breakpoint before element is rendered
  React.useLayoutEffect(() => {
    handleResizeChange();
  }, [handleResizeChange]);

  const resizeObserver = React.useMemo(
    () => new ResizeObserver(handleResizeChange), // Can optimize this by adding a debounce function
    [handleResizeChange],
  );

  React.useEffect(() => {
    if (containerRef?.current) {
      resizeObserver.observe(containerRef.current);
    }
  }, [resizeObserver]);

  return (
    <ViewportContext.Provider
      value={{
        breakpoint,
        ...containerSize,
        showMobileView: breakpoint <= BreakpointSize.small,
      }}
    >
      <view.div ref={containerRef} uses={styles}>
        {children}
      </view.div>
    </ViewportContext.Provider>
  );
};
